/**
 * Navegación fija de EDSuite Web.
 *
 * Los módulos y categorías **no** se declaran aquí: viven en la base de datos
 * (`modulo_categorias` + `modulos` + `plan_modulos`) y llegan por
 * `/api/modulos_disponibles`, ya filtrados por los permisos del usuario y el
 * plan contratado. Así el menú y la pantalla de inicio siempre coinciden con lo
 * que el backend va a autorizar.
 *
 * Este archivo sólo describe los accesos que no son módulos de negocio.
 */

import {
  CircleQuestionMark,
  FileClock,
  Headset,
  LayoutGrid,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

import { APP_META } from './app';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Sólo activo con coincidencia exacta de ruta (evita resaltar con subrutas). */
  end?: boolean;
}

/**
 * Enlace del shell que sale de la SPA: una ruta del panel anterior o una URL
 * absoluta (ej. la mesa de ayuda).
 */
export interface ShellLink {
  label: string;
  icon: LucideIcon;
  /** Ruta dentro de EDSuite Web (el módulo ya está migrado). */
  to?: string;
  /** Ruta en el panel anterior; se compone con `VITE_LEGACY_URL`. */
  legacyPath?: string;
  /** URL absoluta, se abre en otra pestaña. */
  href?: string;
  /**
   * Oculta el enlace a los operadores de turno (grupo 2), replicando el
   * comportamiento del panel anterior.
   */
  hideForOperatorShift?: boolean;
}

/** Grupo de ion_auth de los operadores de turno. */
export const OPERATOR_SHIFT_GROUP_ID = 2;

export const PRIMARY_NAV: readonly NavItem[] = [
  { label: 'Módulos', to: '/inicio', icon: LayoutGrid, end: true },
];

/**
 * Atajos del producto.
 *
 * Sólo el Asistente IA: los demás accesos del panel anterior siguen ahí hasta
 * que se porten, y *Turnos en vivo* se llega desde la categoría *Dashboard* del
 * catálogo y desde la tarjeta *Turnos activos* del dashboard.
 *
 * Nota: el panel anterior también listaba aquí "Dashboard ejecutivo" y
 * "Reportes"; en la web nueva ya se llega a ellos por el acceso fijo
 * *Dashboard* y por la categoría *Reportes*, así que no se duplican.
 */
export const QUICK_LINKS: readonly ShellLink[] = [
  {
    label: 'Asistente IA',
    icon: Sparkles,
    to: '/asistente',
    hideForOperatorShift: true,
  },
];

/** Enlaces de ayuda del producto. */
export const SUPPORT_LINKS: readonly ShellLink[] = [
  {
    label: 'Actualizaciones',
    icon: FileClock,
    to: '/actualizaciones',
    hideForOperatorShift: true,
  },
  {
    label: 'Preguntas frecuentes',
    icon: CircleQuestionMark,
    to: '/faq',
    hideForOperatorShift: true,
  },
  {
    label: 'Soporte técnico',
    icon: Headset,
    href: APP_META.supportUrl,
    hideForOperatorShift: true,
  },
];

/** ¿Es un operador de turno? (grupo 2 de ion_auth) */
export function isOperatorShift(groupIds: readonly number[] | undefined): boolean {
  return Boolean(groupIds?.includes(OPERATOR_SHIFT_GROUP_ID));
}

const FALLBACK_TITLE = 'Panel de control';

/**
 * Títulos de pantallas que existen pero no están en el menú.
 * `/turnos-en-vivo` se abre desde el catálogo (categoría *Dashboard*) y desde la
 * tarjeta *Turnos activos* del dashboard, así que no tiene entrada de menú.
 * Las pantallas de metas se abren desde el módulo `metas` del catálogo, por eso
 * se resuelven por prefijo de ruta (el orden importa: primero las más largas).
 */
const PREFIJOS_TITULOS: readonly { prefijo: string; titulo: string }[] = [
  { prefijo: '/turnos-en-vivo', titulo: 'Turnos en vivo' },
  { prefijo: '/dashboard', titulo: 'Dashboard' },
  { prefijo: '/autoservicio/turnos', titulo: 'Autoservicio · Operación por turno' },
  { prefijo: '/autoservicio/operaciones', titulo: 'Autoservicio · Operaciones' },
  { prefijo: '/autoservicio/cola', titulo: 'Autoservicio · Cola de envío' },
  { prefijo: '/autoservicio/saldos', titulo: 'Autoservicio · Saldos a favor' },
  { prefijo: '/autoservicio', titulo: 'Autoservicio' },
  { prefijo: '/metas/listado', titulo: 'Listado de metas' },
  { prefijo: '/metas/nueva', titulo: 'Nueva meta' },
  { prefijo: '/metas/reportes', titulo: 'Reportes de metas' },
  { prefijo: '/metas/configuracion', titulo: 'Configuración de metas' },
  { prefijo: '/metas', titulo: 'Metas' },
  { prefijo: '/monitoreo/medicion-tanques', titulo: 'Medición de Tanques Controlador' },
  { prefijo: '/monitoreo/calibracion/reporte/detalles', titulo: 'Detalles de calibración' },
  { prefijo: '/monitoreo/calibracion/reporte', titulo: 'Reporte de calibración' },
  { prefijo: '/monitoreo/calibracion', titulo: 'Códigos de calibración' },
  { prefijo: '/monitoreo/ric/visual', titulo: 'RIC · Reporte visual' },
  { prefijo: '/monitoreo/ric/osinerming', titulo: 'RIC · Osinerming' },
  { prefijo: '/monitoreo/ric', titulo: 'RIC · Medidas' },
  { prefijo: '/monitoreo/pts2', titulo: 'Reporte Controlador Ventas' },
  { prefijo: '/monitoreo/surtidor', titulo: 'Surtidor en vivo' },
  { prefijo: '/monitoreo/tanques', titulo: 'Tanques en vivo' },
  { prefijo: '/monitoreo/conciliacion', titulo: 'Conciliación surtidores - tanques' },
  { prefijo: '/monitoreo', titulo: 'Monitoreo y control' },
  { prefijo: '/configuracion/empresa', titulo: 'Empresa' },
  { prefijo: '/configuracion/cuentas-bancarias', titulo: 'Cuentas bancarias' },
  { prefijo: '/configuracion/bancos', titulo: 'Bancos' },
  { prefijo: '/configuracion/tarjetas', titulo: 'Tarjetas' },
  { prefijo: '/configuracion/monedas', titulo: 'Monedas' },
  { prefijo: '/configuracion/tipos-documento', titulo: 'Tipos de documento' },
  { prefijo: '/configuracion/series-documento', titulo: 'Series de documento' },
  { prefijo: '/configuracion/metodos-pago-detraccion', titulo: 'Métodos de pago · detracción' },
  { prefijo: '/configuracion/metodos-pago', titulo: 'Métodos de pago' },
  { prefijo: '/configuracion/tipos-detraccion', titulo: 'Tipos de detracción' },
  { prefijo: '/configuracion/turnos', titulo: 'Turnos' },
  { prefijo: '/configuracion/nfc', titulo: 'Gestión de NFC' },
  { prefijo: '/configuracion/pagos-cpe', titulo: 'Pagos CPE' },
  { prefijo: '/operaciones/terminales', titulo: 'Terminales POS' },
  { prefijo: '/operaciones/usuarios', titulo: 'Usuarios' },
  { prefijo: '/operaciones/grupos', titulo: 'Grupos' },
  { prefijo: '/mantenimiento/categorias', titulo: 'Categorías de mantenimiento' },
  { prefijo: '/mantenimiento/activos', titulo: 'Activos' },
  { prefijo: '/mantenimiento/sub-activos', titulo: 'Sub-activos' },
  { prefijo: '/mantenimiento/tareas', titulo: 'Tareas de mantenimiento' },
  { prefijo: '/mantenimiento/reportes', titulo: 'Reportes de mantenimiento' },
  { prefijo: '/mantenimiento', titulo: 'Mantenimiento' },
  { prefijo: '/logistica/canjes-cardplus', titulo: 'Canjes CardPlus' },
  { prefijo: '/logistica/asignaciones', titulo: 'Asignaciones' },
  { prefijo: '/logistica/documentos', titulo: 'Documentos' },
  { prefijo: '/logistica/notas-venta', titulo: 'Notas de Venta' },
  { prefijo: '/logistica/ventas-market', titulo: 'Ventas Market' },
  { prefijo: '/logistica/ventas-credito', titulo: 'Ventas Crédito' },
  { prefijo: '/logistica/pagos-adelantados-documentos', titulo: 'Documentos de pago adelantado' },
  { prefijo: '/logistica/pagos-adelantados', titulo: 'Pagos adelantados' },
  { prefijo: '/logistica/bovedas', titulo: 'Bóvedas' },
  { prefijo: '/logistica/gastos', titulo: 'Gastos' },
  { prefijo: '/logistica/ingresos-extras', titulo: 'Ingresos Extras' },
  { prefijo: '/logistica/notas-credito', titulo: 'Notas de Crédito' },
  { prefijo: '/gestion/inventario/entradas', titulo: 'Inventario · Entradas' },
  { prefijo: '/gestion/inventario/salidas', titulo: 'Inventario · Salidas' },
  { prefijo: '/gestion/inventario', titulo: 'Inventario' },
  { prefijo: '/gestion/clientes/saldos', titulo: 'Clientes · Créditos y vales' },
  { prefijo: '/gestion/clientes/mensajes', titulo: 'Clientes · Mensajes' },
  { prefijo: '/gestion/clientes', titulo: 'Clientes' },
  { prefijo: '/reportes/diferencia-diaria', titulo: 'Diferencia diaria de combustibles' },
  { prefijo: '/gestion/productos', titulo: 'Productos' },
  { prefijo: '/gestion/clientes-app', titulo: 'Clientes APP' },
  { prefijo: '/gestion/compras/proveedores', titulo: 'Compras · Proveedores' },
  { prefijo: '/gestion/compras', titulo: 'Compras' },
  { prefijo: '/gestion/fletes', titulo: 'Fletes' },
  { prefijo: '/gestion/creditos', titulo: 'Créditos' },
  { prefijo: '/gestion/administracion/comprobantes/crear', titulo: 'Administración · Generar comprobante' },
  { prefijo: '/gestion/administracion/comprobantes', titulo: 'Administración · Comprobantes' },
  { prefijo: '/gestion/administracion/canjes-notas', titulo: 'Administración · Canjes masivos de notas' },
  { prefijo: '/gestion/administracion/boletaje-masivo', titulo: 'Administración · Boletaje masivo' },
  { prefijo: '/gestion/administracion/generador-masivo', titulo: 'Administración · Generador masivo' },
  { prefijo: '/gestion/administracion/consumo', titulo: 'Administración · Consumo por cliente' },
  { prefijo: '/gestion/administracion', titulo: 'Administración' },
  { prefijo: '/pricing/precios', titulo: 'Control de Precios' },
  { prefijo: '/pricing/estadisticas', titulo: 'Estadísticas de precios' },
  { prefijo: '/pricing/scraping', titulo: 'Consultas' },
  { prefijo: '/pricing/configuracion', titulo: 'Configuración de precios' },
  { prefijo: '/pricing', titulo: 'Pricing' },
  { prefijo: '/reportes/historial-precios', titulo: 'Historial de precios' },
  { prefijo: '/reportes/documentos', titulo: 'Documentos' },
  { prefijo: '/reportes/venta-productos', titulo: 'Ventas de productos' },
  { prefijo: '/reportes/gastos', titulo: 'Reporte de gastos' },
  { prefijo: '/reportes/ingreso-boveda', titulo: 'Entrada bóveda' },
  { prefijo: '/reportes/ventas-cliente', titulo: 'Ventas y descuentos por cliente' },
  { prefijo: '/reportes/bitacora-izipay', titulo: 'Bitácora IZIPAY' },
  { prefijo: '/reportes/placas-clientes', titulo: 'Placas por cliente' },
  { prefijo: '/reportes/consumo-interno', titulo: 'Consumo interno ejecutivo' },
  { prefijo: '/reportes/facturas-pendientes', titulo: 'Facturas pendientes de pago' },
  { prefijo: '/reportes/serie-fecha-producto', titulo: 'Comprobantes por serie' },
  { prefijo: '/reportes/kardex', titulo: 'Kardex del producto' },
  { prefijo: '/reportes/creditos-cliente', titulo: 'Créditos por cliente' },
  { prefijo: '/reportes/faltantes-personal', titulo: 'Faltante del personal' },
  { prefijo: '/reportes/descuentos-activos', titulo: 'Descuentos y recargos activos' },
  { prefijo: '/reportes/productividad-turnos', titulo: 'Productividad por turnos' },
  { prefijo: '/reportes/movimientos-cuenta', titulo: 'Movimientos en cuenta' },
  { prefijo: '/reportes/nubecont', titulo: 'Nubecont detallado' },
  { prefijo: '/reportes/compra-productos', titulo: 'Compras por producto' },
  { prefijo: '/reportes/series-globales-mes', titulo: 'Series globales del mes' },
  { prefijo: '/finanzas/caja-chica', titulo: 'Caja Chica' },
  { prefijo: '/finanzas/control-efectivo', titulo: 'Control Efectivo' },
  { prefijo: '/finanzas/ingresos', titulo: 'Ingresos administrativos' },
  { prefijo: '/finanzas/egresos', titulo: 'Egresos administrativos' },
  { prefijo: '/finanzas/transferencias', titulo: 'Transferencias' },
  { prefijo: '/finanzas/pagos-operadores', titulo: 'Pagos de Operadores' },
  { prefijo: '/finanzas/izipay', titulo: 'Izipay' },
  { prefijo: '/finanzas/cuadres', titulo: 'Cuadres' },
  { prefijo: '/finanzas/registro-compras', titulo: 'Registro de Compras 8.1' },
  { prefijo: '/finanzas/registro-ventas', titulo: 'Registro de Ventas 14.1' },
  { prefijo: '/finanzas/contabilidad', titulo: 'Contabilidad' },
  { prefijo: '/finanzas/exportar-concar', titulo: 'Exportar Concar' },
  { prefijo: '/finanzas/kardex', titulo: 'Kardex' },
  { prefijo: '/finanzas/kardex-valorizado', titulo: 'Kardex Valorizado' },
  { prefijo: '/finanzas/movimientos', titulo: 'Movimientos de cuenta' },
  { prefijo: '/reportes/turnos', titulo: 'Turnos' },
  { prefijo: '/reportes/ventas-gnv', titulo: 'Ventas de GNV' },
  { prefijo: '/reportes/ventas-por-metodo', titulo: 'Ventas por método de pago' },
  { prefijo: '/reportes', titulo: 'Reportes' },
  { prefijo: '/configuracion/lados', titulo: 'Lados de máquinas' },
  { prefijo: '/configuracion/maquinas', titulo: 'Máquinas' },
  { prefijo: '/configuracion/mangueras', titulo: 'Mangueras del controlador' },
  { prefijo: '/configuracion/ric-automation', titulo: 'Automatización de RIC' },
  { prefijo: '/configuracion/logs', titulo: 'Logs del sistema' },
  { prefijo: '/configuracion/operadores', titulo: 'Operadores' },
  { prefijo: '/configuracion/planes', titulo: 'Gestión de planes' },
  { prefijo: '/configuracion/modulos', titulo: 'Módulos del sistema' },
  { prefijo: '/configuracion/servicios/documentos', titulo: 'Comprobantes electrónicos' },
  { prefijo: '/configuracion/servicios', titulo: 'Servicios de la estación' },
  { prefijo: '/turno/documentos', titulo: 'Documentos del turno' },
  { prefijo: '/turno/notas', titulo: 'Notas de venta del turno' },
  { prefijo: '/turno/creditos', titulo: 'Vales créditos del turno' },
  { prefijo: '/turno', titulo: 'Mi turno' },
  { prefijo: '/bonus/transacciones', titulo: 'Bonus · Transacciones' },
  { prefijo: '/bonus/balance', titulo: 'Bonus · Balance' },
  { prefijo: '/bonus/acumular', titulo: 'Bonus · Acumular' },
  { prefijo: '/bonus/canje', titulo: 'Bonus · Canje' },
  { prefijo: '/configuracion', titulo: 'Configuración' },
];

/** Título de la pantalla activa, para la barra superior. */
export function resolvePageTitle(pathname: string): string {
  const exact = PRIMARY_NAV.find((item) => item.to === pathname);
  if (exact) return exact.label;

  const shell =
    QUICK_LINKS.find((item) => item.to === pathname) ??
    SUPPORT_LINKS.find((item) => item.to === pathname);
  if (shell) return shell.label;

  const porPrefijo = PREFIJOS_TITULOS.find(
    (entrada) => pathname === entrada.prefijo || pathname.startsWith(`${entrada.prefijo}/`),
  );
  if (porPrefijo) return porPrefijo.titulo;

  if (pathname.startsWith('/inicio/')) return 'Módulos';

  return FALLBACK_TITLE;
}
